<template>
	<div
		class="app-dropdown"
		:class="{
			'app-dropdown--disabled' : $attrs.disabled,
			'app-dropdown--error' : hasError
		}"
		v-on-clickaway="closeOptions"
	>
		<div
			v-if="$slots.default"
			class="app-dropdown__label"
		>
			<span
				v-if="$attrs.required"
				class="require-mark"
			>*</span>
			<slot></slot>
		</div>

		<div class="app-dropdown__wrapper">
			<div
				class="app-dropdown__toggle"
				@click="toggle"
			>
				<input
					type="text"
					class="app-dropdown__hidden"
					:value="hasValue ? 1 : ''"
					:required="$attrs.required"
					@invalid="setInvalidMessage"
				>

				<div class="app-dropdown__selected" v-if="hasValue">
					<template v-if="isMultiple">
						<div
							v-for="(option, index) in selected"
							:key="index"
						>
							<slot name="selected" :props="{option, remove}">
								<span
									class="app-dropdown__value app-dropdown__value--tag"
								>
									{{ option[displayProp] || option }}

									<span
										class="app-dropdown__remove"
										@click.stop="remove(option)"
									>
										&times;
									</span>
								</span>
							</slot>
						</div>
					</template>

					<template v-else>
						<slot name="selected" :props="{option: selected, remove}">
							<span class="app-dropdown__value">
								{{ selected[displayProp] || selected }}
							</span>
						</slot>
					</template>
				</div>

				<div
					v-else
					class="app-dropdown__placeholder"
					:class="{'app-dropdown__placeholder--empty' : !$attrs.placeholder}"
					v-text="$attrs.placeholder || ' '"
				/>

				<div
					class="app-dropdown__arrow"
					:class="{'app-dropdown__arrow--up' : open}"
				/>
			</div>

			<div
				v-if="open"
				class="app-dropdown__options-wrapper"
				:class="{'app-dropdown__options-wrapper--top' : getPositionReference().isTop,
						'app-dropdown__options-wrapper--right' : getPositionReference().isRight}"
			>
				<div
					v-if="searchable"
					class="app-dropdown__search"
				>
					<input
						type="text"
						placeholder="Search"
						v-model="search"
						@input="$emit('search', $event.target.value)"
					>

					<AppIcon
						class="icon"
						icon-color="#778395"
						icon-name="search"
					/>
				</div>

				<ul
					class="app-dropdown__options"
					v-on:scroll="handleScroll"
				>
					<li
						class="app-dropdown__option"
						:class="{'app-dropdown__option--selected' : isSelected(option)}"
						v-for="(option, index) in options"
						:key="index"
						v-show="checkSearchMatch(option[displayProp] || option)"
						@click="select(option)"
					>
						<span
							v-if="checkbox"
							class="checkbox"
						></span>

						<slot
							name="option"
							:props="{option,
								isSelected:  isSelected(option)}"
						>
							{{option[displayProp] || option}}
						</slot>
					</li>
					<li
						v-show="loading"
						class="app-dropdown__empty"
					>
						<slot name="loading">
							{{ 'Loading...' }}
						</slot>
					</li>
					<li
						v-show="isEmpty"
						class="app-dropdown__empty"
					>
						<slot name="empty">
							<span>No item found</span>
						</slot>
					</li>
				</ul>
			</div>
		</div>

		<p
			v-if="hasError"
			class="app-dropdown__error"
		>
			{{ invalidMessage || error}}
		</p>
	</div>
</template>

<script>
export default {
	props: {
		value: {
			default: null
		},
		options: {
			type: Array,
			default: () => []
		},
		displayProp: String,
		searchable: Boolean,
		loading: Boolean,
		checkbox: Boolean,
		error: String
	},

	watch: {
		value (val) {
			if (val) this.invalidMessage = null
		}
	},

	data () {
		return {
			open: false,
			search: '',
			invalidMessage: null
		}
	},

	computed: {
		selected () {
			if (this.value) return this.value
			return this.isMultiple ? [] : ''
		},

		hasError () {
			return this.error || this.invalidMessage
		},

		isEmpty () {
			return !this.loading && this.options.every(i => {
				return !this.checkSearchMatch(i[this.displayProp] || i)
			})
		},

		isMultiple () {
			return this.$attrs.multiple
		},

		hasValue () {
			if (this.selected instanceof Array && this.selected.length === 0) return false
			if (this.selected.constructor === Object && Object.keys(this.selected).length === 0) return false
			return !!this.selected
		}
	},

	methods: {
		select (option) {
			this.invalidMessage = null
			const isSelected = this.isSelected(option)
			if (!isSelected) {
				if (this.isMultiple) {
					const newVal = [...this.selected]
					newVal.push(option)
					this.$emit('input', newVal)
				} else {
					this.$emit('input', option)
				}
			} else {
				if (this.$attrs.required === undefined &&
					!this.isMultiple) this.remove(option)
			}
		},

		remove (option) {
			if (this.isMultiple) {
				const newVal = this.selected.filter(item => {
					return JSON.stringify(item) !== JSON.stringify(option)
				})
				this.$emit('input', newVal)
			} else {
				this.$emit('input', null)
			}
		},

		isSelected (option) {
			if (this.selected instanceof Array) {
				return this.selected.some(item => {
					return JSON.stringify(item) === JSON.stringify(option)
				})
			} else {
				return JSON.stringify(this.selected) === JSON.stringify(option)
			}
		},

		toggle () {
			this.open = !this.open
			this.search = ''
		},

		checkSearchMatch (option) {
			return String(option)
				.toLowerCase()
				.includes(this.search.toLowerCase())
		},

		setInvalidMessage (event) {
			event.preventDefault()
			if (event.target.validity.valueMissing) this.invalidMessage = this.$t('error_fill_up_field')
		},

		getPositionReference () {
			const elementPosition = this.$el.getBoundingClientRect()
			const isTop = window.innerHeight - elementPosition.top < elementPosition.top
			const isRight = window.innerWidth - elementPosition.left < elementPosition.left
			return { isTop, isRight }
		},

		handleScroll (event) {
			if (!this.$listeners['end-of-scroll']) return
			let e = event.target
			if (e.scrollTop + e.offsetHeight >= e.scrollHeight) {
				this.$emit('end-of-scroll', event)
			}
		},

		closeOptions () {
			this.open = false
		}
	}
}
</script>

<style lang="scss" scoped>
.app-dropdown {
	outline: none;
	display: inline-block;

	&__wrapper {
		width: 100%;
		position: relative;
		color: $default;
	}

	&__hidden {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		pointer-events: none;
		z-index: 0;
		opacity: 0;
	}

	&__label {
		margin-bottom: .5em;

		.require-mark {
			color: $danger;
		}
	}

	&__toggle {
		display: flex;
		align-items: center;
		position: relative;
		width: 100%;
		border-radius: 3px;
		border: 1px solid $default-light;
		background-color: #fff;
		user-select: none;
		cursor: pointer;
	}

	&__arrow {
		flex-grow: 0;
		width: .5em;
		height: .5em;
		border: solid $default-light;
		border-width: 0 .1em .1em 0;
		transform: rotate(45deg);
		pointer-events: none;
		margin: 0 8px;

		&--up { transform: rotate(-135deg); }
	}

	&__placeholder, &__selected {
		flex: 1 1 auto;
		font-size: 1em;
	}

	&__placeholder {
		color: $default-light;
		padding: 5px 8px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;

		&--empty { white-space: pre-wrap; }
	}

	&__selected {
		display: flex;
		overflow-x: hidden;
	}

	&__toggle:hover &__selected {
		overflow-x: auto;
	}

	&__selected > * {
		flex-grow: 0;
		flex-shrink: 0;
		margin: 4px;
	}

	&__value {
		padding: 1px 3px;
		border-radius: 3px;
		display: inline-block;

		&--tag {
			background-color: $primary-light;
			color: $primary;
		}

	}

	&__remove {
		pointer-events: all;
		display: inline-block;
		color: $primary;
		cursor: pointer;
	}

	&__options-wrapper {
		position: absolute;
		top: 100%;
		left: 0;
		width: auto;
		min-width: 100%;
		margin: 5px 0;
		border: 1px solid $default-light;
		border-radius: 3px;
		background-color: #fff;
		z-index: 999;

		&--top {
			top: auto;
			bottom: 100%;
		}

		&--right {
			left: auto;
			right: 0;
		}
	}

	&__options {
		list-style: none;
		width: 100%;
		max-height: 150px;
		overflow-x: hidden;
		overflow-y: auto;
	}

	&__option,
	&__empty {
		padding: 5px 8px;
		user-select: none;
	}

	&__option {
		cursor: pointer;
		display: flex;
		align-items: center;
		line-height: 1em;

		.checkbox {
			display: flex;
			justify-content: center;
			align-items: center;
			border: 1px solid $default-light;
			background-color: #fff;
			height: 1em;
			width: 1em;
			margin-right: 8px;
			border-radius: 3px;
			padding-bottom: .1em;
			pointer-events: none;

			&:after {
				content: '';
				display: block;
				width: .2em;
				height: .4em;
				border: solid #fff;
				border-width: 0 .1em .1em 0;
				transform-origin: center;
				transform: rotate(45deg);
			}
		}

		&--selected {
			color: $primary;

			.checkbox:after {
				border-color: $primary;
			}
		}

		&:hover {
			background-color: $primary;
			color: #fff;
		}

		> *:not(:first-child) {
			margin-left: 8px;
		}
	}

	&__search {
		display: flex;
		align-items: center;
		padding: 5px 0;
		margin: 5px 8px;
		border: solid $default-light;
		border-width: 0 0 .5px 0;

		input {
			flex: 1 1 auto;
			font-size: 1em;
			padding-right: 8px;
			outline: none;
			border: none;

			&::placeholder {
				color: $default-light;
			}
		}

		.icon {
			width: 1em;
			height: 1em;
		}
	}

	&:hover &__toggle,
	&:hover &__options-wrapper {
		border-color: $primary;
	}

	&--disabled { pointer-events: none; }

	&--disabled &__toggle {
		opacity: 0.5;
	}

	&--error &__toggle,
	&--error:hover &__toggle {
		border-color: $danger;
	}

	&__error {
		text-align: right;
		color: $danger;
		margin-top: .5em;
	}
}
</style>
